/**
 * Client Query Repository
 *
 * Handles all read operations for clients.
 * This repository is responsible for fetching client data from the database.
 */

import { BaseRepository, RepositoryResult, PaginatedResult, QueryOptions, PaginationParams } from './BaseRepository';
import { getDbClient } from '@/server/lib/database';
import { Client } from '@/shared/types';
import { mapDatabaseClientToClient } from '@/shared/services/dataMapper';

/**
 * Database Client entity
 */
interface DatabaseClient {
  id: string;
  user_id: string;
  name: string;
  email: string;
  phone: string;
  address?: string;
  company?: string;
  tax_id?: string;
  logo_url?: string;
  notes?: string;
  created_at: string;
  updated_at?: string;
  is_deleted?: boolean;
}

/**
 * Client Query Repository
 * Provides read-only operations for the clients table
 */
export class ClientQueryRepository extends BaseRepository<Client> {
  protected tableName = 'clients' as const;

  /**
   * Find all clients for a user with pagination and filtering
   */
  async findAll(
    userId: string,
    options?: QueryOptions & PaginationParams
  ): Promise<RepositoryResult<PaginatedResult<Client>>> {
    try {
      const db = getDbClient();
      const { page, limit } = this.parsePagination(options);
      const { from, to } = this.getRange(page, limit);

      // Build query
      let query = db
        .from(this.tableName)
        .select('*', { count: 'exact' })
        .eq('user_id', userId)
        .is('is_deleted', false);

      // Apply filters
      if (options?.filters) {
        query = this.applyFilters(query, options.filters);
      }

      // Apply sorting
      if (options?.orderBy) {
        query = query.order(options.orderBy, { ascending: options.ascending ?? false });
      } else {
        query = query.order('created_at', { ascending: false });
      }

      // Apply pagination
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) {
        throw error;
      }

      const clients = (data || []).map((dbClient) =>
        mapDatabaseClientToClient(dbClient as DatabaseClient)
      );

      const pagination = this.buildPagination(page, limit, count || 0);

      return {
        success: true,
        data: {
          data: clients,
          pagination,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'fetching clients'),
      };
    }
  }

  /**
   * Find a single client by ID
   */
  async findById(id: string): Promise<RepositoryResult<Client>> {
    try {
      const db = getDbClient();

      const { data, error } = await db
        .from(this.tableName)
        .select('*')
        .eq('id', id)
        .is('is_deleted', false)
        .single();

      if (error) {
        throw error;
      }

      if (!data) {
        return {
          success: false,
          error: 'Client not found',
        };
      }

      const client = mapDatabaseClientToClient(data as DatabaseClient);

      return {
        success: true,
        data: client,
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'fetching client'),
      };
    }
  }

  /**
   * Find clients by user ID (alias for findAll with default options)
   */
  async findByUserId(userId: string): Promise<RepositoryResult<Client[]>> {
    try {
      const db = getDbClient();

      const { data, error } = await db
        .from(this.tableName)
        .select('*')
        .eq('user_id', userId)
        .is('is_deleted', false)
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      const clients = (data || []).map((dbClient) =>
        mapDatabaseClientToClient(dbClient as DatabaseClient)
      );

      return {
        success: true,
        data: clients,
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'fetching clients'),
      };
    }
  }

  /**
   * Search clients by name, email, or phone
   */
  async search(
    userId: string,
    query: string,
    options?: PaginationParams
  ): Promise<RepositoryResult<PaginatedResult<Client>>> {
    try {
      const db = getDbClient();
      const { page, limit } = this.parsePagination(options);
      const { from, to } = this.getRange(page, limit);

      const searchTerm = `%${query}%`;

      const { data, error, count } = await db
        .from(this.tableName)
        .select('*', { count: 'exact' })
        .eq('user_id', userId)
        .is('is_deleted', false)
        .or(`name.ilike.${searchTerm},email.ilike.${searchTerm},phone.ilike.${searchTerm}`)
        .order('name', { ascending: true })
        .range(from, to);

      if (error) {
        throw error;
      }

      const clients = (data || []).map((dbClient) =>
        mapDatabaseClientToClient(dbClient as DatabaseClient)
      );

      const pagination = this.buildPagination(page, limit, count || 0);

      return {
        success: true,
        data: {
          data: clients,
          pagination,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'searching clients'),
      };
    }
  }

  /**
   * Get total count of clients for a user
   */
  async count(userId: string): Promise<RepositoryResult<number>> {
    try {
      const db = getDbClient();

      const { count, error } = await db
        .from(this.tableName)
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .is('is_deleted', false);

      if (error) {
        throw error;
      }

      return {
        success: true,
        data: count || 0,
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'counting clients'),
      };
    }
  }

  /**
   * Find client by email
   */
  async findByEmail(userId: string, email: string): Promise<RepositoryResult<Client>> {
    try {
      const db = getDbClient();

      const { data, error } = await db
        .from(this.tableName)
        .select('*')
        .eq('user_id', userId)
        .eq('email', email)
        .is('is_deleted', false)
        .maybeSingle();

      if (error) {
        throw error;
      }

      if (!data) {
        return {
          success: false,
          error: 'Client not found',
        };
      }

      const client = mapDatabaseClientToClient(data as DatabaseClient);

      return {
        success: true,
        data: client,
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'fetching client by email'),
      };
    }
  }

  /**
   * Find clients by company
   */
  async findByCompany(
    userId: string,
    company: string,
    options?: PaginationParams
  ): Promise<RepositoryResult<PaginatedResult<Client>>> {
    try {
      const db = getDbClient();
      const { page, limit } = this.parsePagination(options);
      const { from, to } = this.getRange(page, limit);

      const { data, error, count } = await db
        .from(this.tableName)
        .select('*', { count: 'exact' })
        .eq('user_id', userId)
        .eq('company', company)
        .is('is_deleted', false)
        .order('name', { ascending: true })
        .range(from, to);

      if (error) {
        throw error;
      }

      const clients = (data || []).map((dbClient) =>
        mapDatabaseClientToClient(dbClient as DatabaseClient)
      );

      const pagination = this.buildPagination(page, limit, count || 0);

      return {
        success: true,
        data: {
          data: clients,
          pagination,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'fetching clients by company'),
      };
    }
  }
}

// Export singleton instance
export const clientQueryRepository = new ClientQueryRepository();
